<template lang="pug">
div
  v-row(align="stretch")
    v-col(md="8")
      v-card
        v-card-title Company highlights
        v-card-text
          v-row(v-for="{ label, value } in companyHighlights", :key="label")
            v-col(md="5")
              span
                b {{ label }}
            v-col(md="5", offset-md="1")
              .d-flex.align-center
                span.ml-4 {{ prettifyValue(value) }}

      v-card.mt-4
        v-card-title Case highlights
        v-card-text
          v-row(v-for="{ label, value } in caseHighlights", :key="label")
            v-col(md="5")
              span
                b {{ label }}
            v-col(md="5", offset-md="1")
              .d-flex.align-center
                span.ml-4 {{ prettifyValue(value) }}

    v-col(md="4")
      v-card(style="height: 100%")
        v-card-title Case progress
        v-card-text
          h1.text-center.display-1.mb-4(
            :class="getTextClass(caseProgressPercent)"
          ) {{ caseProgressPercent }}%
          v-row(
            v-for="{ label, value } in caseProgress",
            :key="label",
            no-gutters
          )
            v-col(md="10")
              span {{ label }}
            v-col(md="2")
              span.ml-2.text-no-wrap(:class="getTextClass(value)") {{ value }}%

  v-row.pa-4
    h2 Mandatory documents
    v-expansion-panels.mt-4(flat, focusable, v-model="panelMandatory")
      Doc(
        v-for="doc in mandatoryDocs",
        :key="'doc_mand:' + doc.ID",
        :data="doc",
        lawyer="1",
        :badge="getBadge(doc)",
        @request="requestAnotherDocumentModal(doc.ID)",
        @approve="approveDocument(doc.ID)",
        @unapprove="unapproveDocument(doc.ID)"
      )

    h2.mt-4 Potential documents
    v-expansion-panels.mt-4(flat, focusable, v-model="panelPotential")
      Doc(
        v-for="doc in recommendedDocs",
        :key="'doc_pot:' + doc.ID",
        :data="doc",
        lawyer="1",
        :badge="getBadge(doc)",
        @request="requestAnotherDocumentModal(doc.ID)",
        @approve="approveDocument(doc.ID)",
        @unapprove="unapproveDocument(doc.ID)"
      )

    v-dialog(v-model="requestDocument.show", width="600px")
      v-card
        v-card-title.headline Request another document
        v-card-text
          v-text-field(
            v-model="requestDocument.comment",
            label="Comment",
            outlined,
            hide-details
          )
        v-card-actions
          v-spacer
          v-btn(text, @click="requestDocument.show = false") cancel
          v-btn(
            color="primary",
            :loading="requestDocument.loading",
            @click="requestAnotherDocument"
          ) request

    h2.mt-4 Recommended documents
    v-expansion-panels.mt-4(flat, focusable, v-model="panelRecommended")
      Doc(
        v-for="doc in potentialDocs",
        :key="'doc_rec:' + doc.ID",
        :data="doc",
        lawyer="1",
        :badge="getBadge(doc)",
        @request="requestAnotherDocumentModal(doc.ID)",
        @approve="approveDocument(doc.ID)",
        @unapprove="unapproveDocument(doc.ID)"
      )
</template>

<script>
import Doc from "@/components/Cases/Sponsorship/views/Documents/Docs/index.vue";
import { mapState } from "vuex";
import { expertAPI } from "@/utils/apiUrls";
import { prettifyValue } from "@/utils/prettifyValue";

export default {
  name: "expert-case-overview",

  components: {
    Doc,
  },

  data: () => ({
    panelPotential: false,
    panelRecommended: false,
    panelMandatory: false,
    requestDocument: {
      id: null,
      loading: false,
      show: false,
      comment: "",
    },
  }),

  mounted() {
    this.$store.dispatch("expert/usercase/sponsorship/refreshOverview");
  },

  computed: {
    ...mapState("expert/usercase/sponsorship", {
      overviewData: (state) => state.overview,
    }),

    documents() {
      return this.overviewData?.docs ?? [];
    },

    caseProgressPercent() {
      return this.overviewData?.case_review_completion.percent ?? 0;
    },

    caseProgress() {
      const case_review_completion = this.overviewData?.case_review_completion;
      return [
        {
          label: "Company profile",
          value: case_review_completion?.company_profile_review,
        },
        {
          label: "Case profile",
          value: case_review_completion?.case_profile_review,
        },
        {
          label: "Documents",
          value: case_review_completion?.documents_review,
        },
        {
          label: "Candidate",
          value: case_review_completion?.candidate_profile_review,
        },
        {
          label: "Cover letter",
          value: case_review_completion?.cover_letter_review,
        },
        {
          label: "Online application",
          value: case_review_completion?.online_application_review,
        },
        {
          label: "Online application submission",
          value: case_review_completion?.online_application_submission,
        },
        {
          label: "Notification sent to the user",
          value: case_review_completion?.notification_sent_to_the_user,
        },
      ].map(({ label, value }) => ({ label, value: value ?? 0 }));
    },

    mandatoryDocs() {
      return this.documents.filter((d) => d.status === "Mandatory");
    },

    recommendedDocs() {
      return this.documents.filter((d) => d.status === "Recommended");
    },

    potentialDocs() {
      return this.documents.filter((d) => d.status === "Potential");
    },

    companyHighlights() {
      const company = this.overviewData?.company;
      const case_highlights = this.overviewData?.case_highlights;
      return [
        {
          label: "Company name",
          value: company?.company_name,
        },
        {
          label: "Client's given name",
          value: company?.personal_data.given_name,
        },
        {
          label: "Client's family name",
          value: company?.personal_data.family_name,
        },
        {
          label: "Client's email",
          value: company?.personal_data.email,
        },
        {
          label: "Company type",
          value: company?.company_type,
        },
        {
          label: "Company selected type",
          value: company?.selected_type,
        },
        {
          label: "Type of body",
          value: case_highlights?.type_body,
        },
        {
          label: "Period of trading",
          value: company?.period_of_trading,
        },
        {
          label: "Model of operations",
          value: company?.premises_status,
        },
        {
          label: "Sector",
          value: company?.sector_operations,
        },
        {
          label: "Number of employees",
          value: company?.number_of_employees,
        },
        {
          label: "Under Regulatory body regulations",
          value: company?.under_regulatory_body_regulations,
        },
        {
          label: "Under Governing body regulations",
          value: company?.under_governing_body_regulations,
        },
        {
          label: "Next accounts made up to",
          value: company?.next_accounts_made_up_to,
        },
      ];
    },

    caseHighlights() {
      const case_overview = this.overviewData?.case_overview;
      const eligibility = case_overview?.eligibility;
      const suitability = case_overview?.suitability;
      const case_highlights = this.overviewData?.case_highlights;
      return [
        {
          label: "Genuine business",
          value: eligibility?.genuine_business,
        },
        {
          label: "Supporting documents",
          value: eligibility?.supporting_docs,
        },
        {
          label: "Operations in the UK",
          value: eligibility?.operations_in_the_uk,
        },
        {
          label: "HR system in place",
          value: suitability?.hr_system_in_place,
        },
        {
          label: "Facilitation of visits and checks",
          value: suitability?.visits_and_checks,
        },
        {
          label: "Genuine vacancy",
          value: suitability?.genuine_vacancy,
        },
        {
          label: "Key personnel",
          value: suitability?.key_personnel,
        },
        {
          label: "No previous non-compliance",
          value: suitability?.previous_non_compliance,
        },
        {
          label: "Absence of criminal convictions",
          value: suitability?.criminal_conviction,
        },
        {
          label: "Identified candidate",
          value: case_highlights?.identified_candidate,
        },
        {
          label: "Status of Candidate's UK Residency",
          value: case_highlights?.status_of_candidates_uk_residency,
        },
        {
          label: "Previous licence application",
          value: case_highlights?.previous_application_status,
        },
        {
          label: "Home Office decision on previous applications",
          value: case_highlights?.home_office_decision_on_previous_applications,
        },
        {
          label: "The reason for unsuccessful application",
          value: case_highlights?.the_reason_for_unsuccessful_application,
        },
        {
          label: "Previous sponsor activities",
          value: case_highlights?.previous_sponsor_activities,
        },
        {
          label: "Company activities",
          value: case_highlights?.company_activities,
        },
        {
          label: "Reason to become a sponsor",
          value: case_highlights?.reason_to_become_a_sponsor,
        },
        {
          label: "Number of unsettled employees",
          value: case_highlights?.number_of_unsettled_employees,
        },
        {
          label: "AO = KC = Level 1",
          value: case_highlights?.ao_kc_level_1,
        },
        {
          label: "AO = KC",
          value: case_highlights?.ao_kc,
        },
        {
          label: "Who will be the Level 1 User?",
          value: case_highlights?.who_will_be_the_level_1_user,
        },
        {
          label: "Is the Authorising Officer a non-settled worker?",
          value:
            case_highlights?.is_the_authorising_officer_a_non_settled_worker,
        },
        {
          label: "Authorising Officer's UK immigration status",
          value: case_highlights?.authorising_officers_uk_immigration_status,
        },
      ];
    },
  },

  methods: {
    percColor(value) {
      if (value < 50) return "red";
      if (value < 99) return "orange";
      return "green";
    },

    getTextClass(value) {
      if (value === 0) return "red--text";
      if (value > 0 && value < 100) return "yellow--text";
      if (value === 100) return "green--text";
    },

    getBadge(doc) {
      switch (doc.decision) {
        case "approved":
          return { color: "success", text: "Approved" };
        case "unapproved":
          return { color: "error", text: "Unapproved" };
        case "requested_change":
          return { color: "warning", text: "Request another document" };
        default:
          return { color: null, text: "" };
      }
    },

    approveDocument(id) {
      this.axios
        .get(
          expertAPI.usercase.sponsorship.documents.ApproveDocumentURL(
            this.caseID,
            id
          )
        )
        .then(() => {
          this.$store.dispatch("expert/usercase/sponsorship/refreshDocuments");
        })
        .catch(console.error);
    },

    unapproveDocument(id) {
      this.axios
        .get(
          expertAPI.usercase.sponsorship.documents.UnapproveDocumentURL(
            this.caseID,
            id
          )
        )
        .then(() => {
          this.$store.dispatch("expert/usercase/sponsorship/refreshDocuments");
        })
        .catch(console.error);
    },

    requestAnotherDocumentModal(id) {
      this.requestDocument.show = true;
      this.requestDocument.id = id;
    },

    requestAnotherDocument() {
      this.requestDocument.loading = true;

      this.axios
        .post(
          expertAPI.usercase.sponsorship.documents.RequestChangeURL(
            this.caseID,
            this.requestDocument.id
          ),
          {
            message: this.requestDocument.comment,
          }
        )
        .then(() => {
          this.requestDocument = {
            loading: false,
            show: false,
            id: null,
            comment: "",
          };
          this.$store.dispatch("expert/usercase/sponsorship/refreshDocuments");
        })
        .catch(console.error);
    },

    prettifyValue(value) {
      return prettifyValue(value);
    },
  },
};
</script>

<style lang="sass" scoped>
.status
  width: 15px
  height: 15px
  border-radius: 50%
</style>
